import { Component, For, Show, createMemo, createSignal } from 'solid-js'
import { snapdom } from '@zumer/snapdom'
import { 
  state, 
  actions, 
  maxEnergy,
  maxCompute,
  totalHwPower,
  totalHwEnergyUse,
  ownedHardwareList,
  trainEfficiency,
  researchEfficiency,
  currentModel,
  currentModelState,
  totalUsers,
  marketPrice,
  revenuePerInference,
  autoInferenceRate,
  autoTrainRate,
  autoResearchRate
} from './store/gameStore'
import { BALANCE, MODELS, UPGRADES, DEBUG_ERAS, UPGRADE_TIERS, getUpgradeTier } from './config/balanceConfig'
import { SaveSystem } from './systems/SaveSystem'
import { Analytics } from './systems/AnalyticsSystem'
import { eventLog } from './store/eventLog'
import { toastStore, toast } from './store/toastStore'
import { t, currentLanguage, setCurrentLanguage } from './i18n'

// Formatting utilities
const formatNumber = (n: number): string => {
  if (n < 1000) return n.toFixed(n < 10 && n > 0 ? 2 : 0)
  if (n < 1_000_000) return (n / 1000).toFixed(1) + 'K'
  if (n < 1_000_000_000) return (n / 1_000_000).toFixed(2) + 'M'
  if (n < 1_000_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B'
  return (n / 1_000_000_000_000).toFixed(2) + 'T'
}

const formatMoney = (n: number): string => {
  if (n < 1000) return '$' + n.toFixed(n < 1 ? 3 : 2)
  return '$' + formatNumber(n)
}

const formatTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

const App: Component = () => {
  const [showSettings, setShowSettings] = createSignal(false)
  const [showDebugEras, setShowDebugEras] = createSignal(false)
  const [showLangDropdown, setShowLangDropdown] = createSignal(false)
  const [upgradeFilter, setUpgradeFilter] = createSignal<'all' | 'energy' | 'compute' | 'hw' | 'train' | 'research' | 'auto'>('all')
  const [mobileTab, setMobileTab] = createSignal<'resources' | 'models' | 'hardware' | 'upgrades'>('resources')
  
  // Share modal state
  const [showShareModal, setShowShareModal] = createSignal(false)
  const [shareImageUrl, setShareImageUrl] = createSignal<string | null>(null)
  
  // Feedback modal state
  const [showFeedback, setShowFeedback] = createSignal(false)
  const [feedbackMessage, setFeedbackMessage] = createSignal('')
  const [feedbackEmail, setFeedbackEmail] = createSignal('')
  const [feedbackStatus, setFeedbackStatus] = createSignal<'idle' | 'sending' | 'success' | 'error' | 'rateLimit'>('idle')
  
  // Victory modal state - shown automatically when victory is achieved, can be dismissed
  const [victoryDismissed, setVictoryDismissed] = createSignal(false)
  const showVictoryModal = createMemo(() => state.victoryAchieved && !victoryDismissed())
  
  // Console collapsed state - collapsed by default on mobile
  const isMobile = () => window.innerWidth <= 900
  const [consoleCollapsed, setConsoleCollapsed] = createSignal(isMobile())

  // Tap particles system
  type Particle = {
    id: number
    x: number
    y: number
    emoji: string
    color: string
  }
  const [particles, setParticles] = createSignal<Particle[]>([])
  let particleId = 0

  const spawnParticles = (e: MouseEvent | TouchEvent, type: 'train' | 'inference' | 'research') => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    let clientX: number, clientY: number
    
    if ('touches' in e && e.touches.length > 0) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else if ('clientX' in e) {
      clientX = e.clientX
      clientY = e.clientY
    } else {
      clientX = rect.left + rect.width / 2
      clientY = rect.top + rect.height / 2
    }

    const config = {
      train: { emoji: '🧠', color: '#3fb950' },
      inference: { emoji: '💰', color: '#58a6ff' },
      research: { emoji: '🔬', color: '#a855f7' }
    }

    const { emoji, color } = config[type]
    
    const newParticle: Particle = {
      id: particleId++,
      x: clientX,
      y: clientY,
      emoji,
      color
    }
    
    setParticles(prev => [...prev, newParticle])
    
    // Remove particle after animation
    setTimeout(() => {
      setParticles(prev => prev.filter(p => p.id !== newParticle.id))
    }, 1200)
  }

  // Current model's quality
  const currentQuality = createMemo(() => currentModelState().quality)
  
  const qualityClass = createMemo(() => {
    const q = currentQuality()
    if (q < BALANCE.CRITICAL_QUALITY) return 'danger'
    if (q < BALANCE.MIN_QUALITY_FOR_USERS) return 'warning'
    return ''
  })

  const canTrain = createMemo(() => !state.paused && actions.canAffordTrain())
  const canInfer = createMemo(() => !state.paused && actions.canInfer())
  const canResearch = createMemo(() => !state.paused && actions.isResearchUnlocked() && actions.canAffordResearch())
  const researchUnlocked = createMemo(() => actions.isResearchUnlocked())

  // Price comparison indicator
  const priceVsMarket = createMemo(() => {
    const market = marketPrice()
    const yours = state.price
    if (yours < market * 0.8) return 'cheap'      // Much cheaper - green
    if (yours > market * 1.2) return 'expensive'  // Much more expensive - red
    return 'fair'                                  // Around market price
  })

  const handleTrain = (e: MouseEvent | TouchEvent) => {
    if (canTrain()) {
      spawnParticles(e, 'train')
    }
    actions.clickTrain()
  }

  const handleInfer = (e: MouseEvent | TouchEvent) => {
    if (canInfer()) {
      spawnParticles(e, 'inference')
    }
    actions.clickInference()
  }

  const handleResearch = (e: MouseEvent | TouchEvent) => {
    if (canResearch()) {
      spawnParticles(e, 'research')
    }
    actions.clickResearch()
  }

  const handleReset = () => {
    if (confirm(t('resetConfirm'))) {
      SaveSystem.clear()
      actions.reset()
    }
  }

  const generateShareImage = async (): Promise<string> => {
    try {
      let targetElement: HTMLElement = document.body
      let container: HTMLDivElement | null = null
      
      // On desktop, create a virtual mobile viewport
      if (!isMobile()) {
        container = document.createElement('div')
        container.style.cssText = `
          position: fixed;
          left: -9999px;
          top: 0;
          width: 390px;
          height: 844px;
          overflow: hidden;
          background: var(--bg-primary);
        `
        
        // Clone the body content
        const clone = document.body.cloneNode(true) as HTMLElement
        clone.style.width = '390px'
        clone.style.minHeight = '844px'
        
        // Force mobile styles by adding a class
        clone.classList.add('force-mobile-view')
        
        container.appendChild(clone)
        document.body.appendChild(container)
        
        targetElement = container
      }
      
      // Capture with snapdom
      const result = await snapdom(targetElement)
      const fullCanvas = await result.toCanvas()
      
      // Clean up virtual viewport
      if (container) {
        document.body.removeChild(container)
      }
      
      // Create a new canvas with top 60% cropped to square
      const cropHeight = Math.floor(fullCanvas.height * 0.6)
      const size = Math.min(fullCanvas.width, cropHeight)
      
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')!
      
      // Draw the top portion, centered horizontally
      const srcX = Math.floor((fullCanvas.width - size) / 2)
      ctx.drawImage(fullCanvas, srcX, 0, size, size, 0, 0, size, size)
      
      // Add URL overlay at bottom
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
      ctx.fillRect(0, size - 40, size, 40)
      ctx.fillStyle = '#3fb950'
      ctx.font = 'bold 16px system-ui, -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('gpt-clicker.pixdeo.com', size / 2, size - 14)
      
      return canvas.toDataURL('image/png')
    } catch (e) {
      console.error('Screenshot failed:', e)
      // Return a simple fallback
      const canvas = document.createElement('canvas')
      canvas.width = 400
      canvas.height = 400
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#1a1a2e'
      ctx.fillRect(0, 0, 400, 400)
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 24px system-ui'
      ctx.textAlign = 'center'
      ctx.fillText('GPT Clicker', 200, 180)
      ctx.fillStyle = '#3fb950'
      ctx.font = '18px system-ui'
      ctx.fillText(formatMoney(state.money), 200, 220)
      ctx.fillText('gpt-clicker.pixdeo.com', 200, 360)
      return canvas.toDataURL('image/png')
    }
  }

  const handleShare = async () => {
    const imageUrl = await generateShareImage()
    setShareImageUrl(imageUrl)
    setShowShareModal(true)
    
    // Track share event
    Analytics.share('modal_opened', {
      playTime: state.playTime,
      money: state.money,
      users: Math.floor(totalUsers()),
      models: state.unlockedModels.length
    })
  }

  const getShareText = () => {
    const users = formatNumber(Math.floor(totalUsers()))
    const money = formatMoney(state.money)
    const model = currentModel().name
    const time = formatTime(state.playTime)
    return `🤖 GPT Clicker\n💰 ${money} | 👥 ${users} users\n🧠 ${model} | ⏱️ ${time}\n\n${t('shareChallenge')}\n\nhttps://gpt-clicker.pixdeo.com`
  }

  const shareToTwitter = () => {
    const text = encodeURIComponent(getShareText())
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank')
    Analytics.share('twitter', { playTime: state.playTime, money: state.money, users: Math.floor(totalUsers()), models: state.unlockedModels.length })
  }

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(getShareText())
    window.open(`https://wa.me/?text=${text}`, '_blank')
    Analytics.share('whatsapp', { playTime: state.playTime, money: state.money, users: Math.floor(totalUsers()), models: state.unlockedModels.length })
  }

  const copyShareImage = async () => {
    try {
      const imageUrl = shareImageUrl()
      
      if (imageUrl && navigator.clipboard.write) {
        // Convert data URL to blob
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ])
        toast.success(t('imageCopied'))
      } else {
        // Fallback - copy text if image not available
        await navigator.clipboard.writeText(getShareText())
        toast.success(t('shareCopied'))
      }
    } catch {
      // Fallback to text
      try {
        await navigator.clipboard.writeText(getShareText())
        toast.success(t('shareCopied'))
      } catch {
        prompt('Copy this text:', getShareText())
      }
    }
    Analytics.share('copy', { playTime: state.playTime, money: state.money, users: Math.floor(totalUsers()), models: state.unlockedModels.length })
  }

  const copyShareText = async () => {
    try {
      await navigator.clipboard.writeText(getShareText())
      toast.success(t('shareCopied'))
    } catch {
      prompt('Copy this text:', getShareText())
    }
    Analytics.share('copy_text', { playTime: state.playTime, money: state.money, users: Math.floor(totalUsers()), models: state.unlockedModels.length })
  }

  const downloadShareImage = () => {
    const url = shareImageUrl()
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = 'gpt-clicker-stats.png'
    a.click()
    Analytics.share('download', { playTime: state.playTime, money: state.money, users: Math.floor(totalUsers()), models: state.unlockedModels.length })
  }

  const handleToastShare = async () => {
    // Open share modal instead of just copying text
    await handleShare()
  }

  const selectLanguage = (lang: 'en' | 'es') => {
    setCurrentLanguage(lang)
    setShowLangDropdown(false)
  }

  const handleSendFeedback = async () => {
    const message = feedbackMessage().trim()
    if (message.length < 3) return
    
    setFeedbackStatus('sending')
    
    try {
      const response = await fetch('https://feedback.pixdeo.com/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          app_id: 'gpt-clicker',
          message: message,
          email: feedbackEmail().trim() || undefined,
        }),
      })
      
      if (response.status === 429) {
        setFeedbackStatus('rateLimit')
        return
      }
      
      if (!response.ok) {
        setFeedbackStatus('error')
        return
      }
      
      setFeedbackStatus('success')
      setFeedbackMessage('')
      setFeedbackEmail('')
      
      // Auto close after success
      setTimeout(() => {
        setShowFeedback(false)
        setFeedbackStatus('idle')
      }, 2000)
    } catch {
      setFeedbackStatus('error')
    }
  }

  // Keyboard shortcuts
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
    
    if (e.key === 't' || e.key === 'T') {
      e.preventDefault()
      actions.clickTrain()
    }
    if (e.key === 'i' || e.key === 'I') {
      e.preventDefault()
      actions.clickInference()
    }
    if (e.key === 'r' || e.key === 'R') {
      e.preventDefault()
      actions.clickResearch()
    }
  }

  document.addEventListener('keydown', handleKeyDown)

  // Check if upgrades should be locked
  const upgradesLocked = createMemo(() => {
    const firstModel = MODELS[0]
    const firstModelQuality = state.models[firstModel.id]?.quality || 0
    return firstModelQuality < 50 && state.unlockedModels.length === 1
  })

  // Count affordable upgrades for notification badge
  const affordableUpgradesCount = createMemo(() => {
    if (upgradesLocked()) return 0
    return UPGRADES.filter(upgrade => {
      const owned = actions.getUpgradeCount(upgrade.id)
      const maxed = upgrade.maxOwned !== -1 && owned >= upgrade.maxOwned
      if (maxed) return false
      return !state.paused && actions.isUpgradeAvailable(upgrade) && actions.canAffordUpgrade(upgrade)
    }).length
  })

  // Get upgrade tag
  const getTag = (effectType: string) => {
    switch (effectType) {
      case 'max_energy': return { text: t('tagEnergy'), class: 'tag-energy' }
      case 'energy_regen': return { text: t('tagEnergy'), class: 'tag-energy' }
      case 'max_compute': return { text: t('tagCompute'), class: 'tag-compute' }
      case 'hw_upgrade': return { text: t('tagHw'), class: 'tag-hw' }
      case 'train_efficiency': return { text: t('tagTrain'), class: 'tag-train' }
      case 'research_efficiency': return { text: t('tagResearch'), class: 'tag-research' }
      case 'auto_inference': return { text: t('tagAuto'), class: 'tag-inference' }
      case 'auto_train': return { text: t('tagAuto'), class: 'tag-train' }
      case 'auto_research': return { text: t('tagAuto'), class: 'tag-research' }
      case 'hw_boost': return { text: t('tagHw'), class: 'tag-hw' }
      default: return { text: '', class: '' }
    }
  }

  return (
    <>
      <header>
        <div class="header-content">
          <div class="header-title" title={t('tooltipTitle')}>
            <img src="/assets/gpt-clicker-logo.png" alt="GPT Clicker Logo" class="header-logo" />
            <h1>{t('title')} <span class="header-version">v{__APP_VERSION__}</span></h1>
          </div>
          <div class="header-stats">
            <span class="stat stat-spent" title={t('tooltipComputeSpent')}>🔥 {formatNumber(state.totalComputeSpent)}</span>
            <span class="stat stat-time" title={t('tooltipPlayTime')}>⏱️ {formatTime(state.playTime)}</span>
          </div>
          <div class="header-spacer" />
          <div class="header-toolbar">
            <button class="toolbar-btn" onClick={handleShare} title={t('shareButton')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
              </svg>
            </button>
            <button class="toolbar-btn" onClick={() => setShowFeedback(true)} title={t('feedback')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
            </button>
            <div class="lang-dropdown-container">
              <button 
                class="toolbar-btn toolbar-btn-lang" 
                onClick={() => setShowLangDropdown(!showLangDropdown())}
                title={t('language')}
              >
                <span class="lang-flag">{currentLanguage() === 'es' ? '🇪🇸' : '🇺🇸'}</span>
                <span class="lang-code">{currentLanguage().toUpperCase()}</span>
              </button>
              <Show when={showLangDropdown()}>
                <div class="lang-dropdown">
                  <button 
                    class={`lang-option ${currentLanguage() === 'en' ? 'active' : ''}`}
                    onClick={() => selectLanguage('en')}
                  >
                    <span class="lang-flag">🇺🇸</span> English
                  </button>
                  <button 
                    class={`lang-option ${currentLanguage() === 'es' ? 'active' : ''}`}
                    onClick={() => selectLanguage('es')}
                  >
                    <span class="lang-flag">🇪🇸</span> Español
                  </button>
                </div>
              </Show>
            </div>
            <button 
              class={`toolbar-btn ${state.paused ? 'active' : ''}`}
              onClick={() => actions.togglePause()}
              title={state.paused ? t('resume') : t('pause')}
            >
              <Show when={state.paused} fallback={
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16"/>
                  <rect x="14" y="4" width="4" height="16"/>
                </svg>
              }>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21"/>
                </svg>
              </Show>
            </button>
            <div class="settings-dropdown-container">
              <button class="toolbar-btn" onClick={() => setShowSettings(!showSettings())} title={t('tooltipSettings')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
                </svg>
              </button>
              <Show when={showSettings()}>
                <div class="settings-dropdown">
                  <Show when={import.meta.env.DEV}>
                    <div class="settings-submenu-container">
                      <button 
                        class="settings-option"
                        onClick={() => setShowDebugEras(!showDebugEras())}
                      >
                        🐛 Debug Era {showDebugEras() ? '▲' : '▼'}
                      </button>
                      <Show when={showDebugEras()}>
                        <div class="settings-submenu">
                          <For each={DEBUG_ERAS}>
                            {(era) => (
                              <button 
                                class="settings-option"
                                onClick={() => {
                                  actions.setDebugEra(era)
                                  setShowDebugEras(false)
                                  setShowSettings(false)
                                }}
                              >
                                {era.name}
                              </button>
                            )}
                          </For>
                        </div>
                      </Show>
                    </div>
                  </Show>
                  <button class="settings-option danger" onClick={handleReset}>
                    🗑️ {t('resetGame')}
                  </button>
                </div>
              </Show>
            </div>
          </div>
        </div>
        
        {/* Mobile Header */}
        <div class="mobile-header">
          <div class="mobile-header-top">
            <div class="mobile-header-title">
              <img src="/assets/gpt-clicker-logo.png" alt="GPT Clicker Logo" class="header-logo" />
              <h1>{t('title')}</h1>
            </div>
            <div class="mobile-header-stats">
              <span class="stat stat-spent">🔥 {formatNumber(state.totalComputeSpent)}</span>
              <span class="stat stat-time">⏱️ {formatTime(state.playTime)}</span>
            </div>
            <div class="mobile-header-actions">
              <button 
                class={`pause-btn ${state.paused ? 'paused' : ''}`}
                onClick={() => actions.togglePause()}
              >
                {state.paused ? '▶' : '⏸'}
              </button>
              <div class="settings-dropdown-container">
                <button class="settings-btn" onClick={() => setShowSettings(!showSettings())}>⚙️</button>
                <Show when={showSettings()}>
                  <div class="settings-dropdown">
                    <button class="settings-option" onClick={() => { handleShare(); setShowSettings(false) }}>
                      📤 {t('shareButton')}
                    </button>
                    <button class="settings-option" onClick={() => { setShowFeedback(true); setShowSettings(false) }}>
                      💬 {t('feedback')}
                    </button>
                    <div class="settings-lang-section">
                      <span class="settings-lang-label">{t('language')}</span>
                      <div class="settings-lang-options">
                        <button 
                          class={`settings-lang-btn ${currentLanguage() === 'en' ? 'active' : ''}`}
                          onClick={() => selectLanguage('en')}
                        >
                          🇺🇸 EN
                        </button>
                        <button 
                          class={`settings-lang-btn ${currentLanguage() === 'es' ? 'active' : ''}`}
                          onClick={() => selectLanguage('es')}
                        >
                          🇪🇸 ES
                        </button>
                      </div>
                    </div>
                    <Show when={import.meta.env.DEV}>
                      <div class="settings-submenu-container">
                        <button 
                          class="settings-option"
                          onClick={() => setShowDebugEras(!showDebugEras())}
                        >
                          🐛 Debug Era {showDebugEras() ? '▲' : '▼'}
                        </button>
                        <Show when={showDebugEras()}>
                          <div class="settings-submenu">
                            <For each={DEBUG_ERAS}>
                              {(era) => (
                                <button 
                                  class="settings-option"
                                  onClick={() => {
                                    actions.setDebugEra(era)
                                    setShowDebugEras(false)
                                    setShowSettings(false)
                                  }}
                                >
                                  {era.name}
                                </button>
                              )}
                            </For>
                          </div>
                        </Show>
                      </div>
                    </Show>
                    <button class="settings-option danger" onClick={handleReset}>
                      🗑️ {t('resetGame')}
                    </button>
                  </div>
                </Show>
              </div>
            </div>
          </div>
          <div class="mobile-stats-grid">
            <div class="mobile-stat">
              <span class="mobile-stat-value stat-money">{formatMoney(state.money)}</span>
              <span class="mobile-stat-label">{t('money')}</span>
            </div>
            <div class="mobile-stat">
              <span class="mobile-stat-value stat-energy">{formatNumber(Math.floor(state.energy))}/{formatNumber(maxEnergy())}</span>
              <span class="mobile-stat-label">{t('energy')}</span>
            </div>
            <div class="mobile-stat">
              <span class="mobile-stat-value stat-compute">{formatNumber(Math.floor(state.compute))}/{formatNumber(maxCompute())}</span>
              <span class="mobile-stat-label">{t('compute')}</span>
            </div>
            <div class="mobile-stat">
              <span class="mobile-stat-value stat-research">{formatNumber(state.research)}</span>
              <span class="mobile-stat-label">{t('research')}</span>
            </div>
            <div class="mobile-stat">
              <span class="mobile-stat-value stat-users">{formatNumber(Math.floor(totalUsers()))}</span>
              <span class="mobile-stat-label">{t('totalUsers')}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Tabs */}
      <div class="mobile-tabs">
        <button 
          class={`mobile-tab ${mobileTab() === 'resources' ? 'active' : ''}`}
          onClick={() => setMobileTab('resources')}
        >{t('mobileResources')}</button>
        <button 
          class={`mobile-tab ${mobileTab() === 'models' ? 'active' : ''}`}
          onClick={() => setMobileTab('models')}
        >{t('mobileModels')}</button>
        <button 
          class={`mobile-tab ${mobileTab() === 'hardware' ? 'active' : ''}`}
          onClick={() => setMobileTab('hardware')}
        >{t('mobileHardware')}</button>
        <button 
          class={`mobile-tab ${mobileTab() === 'upgrades' ? 'active' : ''}`}
          onClick={() => setMobileTab('upgrades')}
        >
          {t('mobileUpgrades')}
          <Show when={affordableUpgradesCount() > 0 && mobileTab() !== 'upgrades'}>
            <span class="tab-badge">{affordableUpgradesCount()}</span>
          </Show>
        </button>
      </div>

      <main>
        {/* Left Column: Resources & Actions */}
        <section class={`panel ${mobileTab() === 'resources' ? 'mobile-visible' : 'mobile-hidden'}`} id="resources-panel">
          <h2>{t('resources')}</h2>
          
          <div class="resources-stats">
            <div class="resource-row" title={t('tooltipMoney')}>
              <span class="resource-label">{t('money')}</span>
              <span class="resource-value stat-money">{formatMoney(state.money)}</span>
            </div>
            <div class="resource-row" title={t('tooltipEnergy')}>
              <span class="resource-label">{t('energy')}</span>
              <span class="resource-value stat-energy">{formatNumber(Math.floor(state.energy))}/{formatNumber(maxEnergy())}</span>
            </div>
            <div class="resource-row" title={t('tooltipCompute')}>
              <span class="resource-label">{t('compute')}</span>
              <span class="resource-value stat-compute">{formatNumber(Math.floor(state.compute))}/{formatNumber(maxCompute())}</span>
            </div>
            <div class="resource-row" title={t('tooltipResearch')}>
              <span class="resource-label">{t('research')}</span>
              <span class="resource-value stat-research">{formatNumber(Math.floor(state.research))}</span>
            </div>
            <div class="resource-row" title={t('tooltipUsers')}>
              <span class="resource-label">{t('totalUsers')}</span>
              <span class="resource-value stat-users">{formatNumber(Math.floor(totalUsers()))}</span>
            </div>
          </div>
          
          <div class="resource">
            <span class="label">{currentModel().name} {t('quality')}:</span>
            <span class="value">{currentQuality().toFixed(1)}%</span>
            <div class="progress-bar">
              <div 
                class={`progress-fill ${qualityClass()}`} 
                style={{ width: `${currentQuality()}%` }}
              />
            </div>
          </div>
          
          <div class="resource price-resource">
            <span class="label">{t('price')} ($/1M tokens):</span>
             <div class="price-display">
              <span class={`value price-${priceVsMarket()}`}>${state.price.toFixed(4)}</span>
              <span class="market-price">Mercado: ${marketPrice().toFixed(4)}</span>
            </div>
            <div class="price-controls">
              <button class="price-btn" onClick={() => actions.decreasePrice()}>-</button>
              <button class="price-btn" onClick={() => actions.increasePrice()}>+</button>
            </div>
          </div>

          <div class="actions">
            <div class={`action-wrapper ${autoTrainRate() > 0 ? 'has-auto' : ''}`}>
              <Show when={autoTrainRate() > 0}>
                <div class={`auto-control auto-train ${state.autoTrainThrottle > 0 ? 'active' : ''}`}>
                  <div class="auto-buttons">
                    <button class="auto-btn" onClick={(e) => { e.stopPropagation(); actions.increaseAutoTrainThrottle() }}>+</button>
                    <button class="auto-btn" onClick={(e) => { e.stopPropagation(); actions.decreaseAutoTrainThrottle() }}>-</button>
                  </div>
                  <span class="auto-label">{(autoTrainRate() * state.autoTrainThrottle / 100).toFixed(1)}/s</span>
                </div>
              </Show>
              <button 
                class={`action-btn primary ${!canTrain() ? 'disabled' : ''}`}
                onClick={handleTrain}
              >
                <div class="btn-left">
                  <span class="btn-title">{t('train')}</span>
                  <span class="btn-subtitle">{formatNumber(actions.getTrainCost())} comp + {BALANCE.TRAIN_ENERGY_COST} energy</span>
                </div>
                <span class="btn-cost">+{(BALANCE.BASE_QUALITY_PER_TRAIN * trainEfficiency()).toFixed(1)}%</span>
              </button>
            </div>
            
            <div class={`action-wrapper ${autoInferenceRate() > 0 ? 'has-auto' : ''}`}>
              <Show when={autoInferenceRate() > 0}>
                <div class={`auto-control auto-inference ${state.autoInferenceThrottle > 0 ? 'active' : ''}`}>
                  <div class="auto-buttons">
                    <button class="auto-btn" onClick={(e) => { e.stopPropagation(); actions.increaseAutoThrottle() }}>+</button>
                    <button class="auto-btn" onClick={(e) => { e.stopPropagation(); actions.decreaseAutoThrottle() }}>-</button>
                  </div>
                  <span class="auto-label">{(autoInferenceRate() * state.autoInferenceThrottle / 100).toFixed(1)}/s</span>
                </div>
              </Show>
              <button 
                class={`action-btn secondary ${!canInfer() ? 'disabled' : ''}`}
                onClick={handleInfer}
              >
                <div class="btn-left">
                  <span class="btn-title">{t('inference')}</span>
                  <span class="btn-subtitle">
                    {totalUsers() >= 1 
                      ? `${BALANCE.INFERENCE_COMPUTE_COST} comp + ${BALANCE.INFERENCE_ENERGY_COST} energy`
                      : t('needUsers')
                    }
                  </span>
                </div>
                <span class="btn-cost">
                  {totalUsers() >= 1 ? `+${formatMoney(revenuePerInference())}` : ''}
                </span>
              </button>
            </div>
            
            <div class={`action-wrapper ${autoResearchRate() > 0 ? 'has-auto' : ''}`}>
              <Show when={autoResearchRate() > 0}>
                <div class={`auto-control auto-research ${state.autoResearchThrottle > 0 ? 'active' : ''}`}>
                  <div class="auto-buttons">
                    <button class="auto-btn" onClick={(e) => { e.stopPropagation(); actions.increaseAutoResearchThrottle() }}>+</button>
                    <button class="auto-btn" onClick={(e) => { e.stopPropagation(); actions.decreaseAutoResearchThrottle() }}>-</button>
                  </div>
                  <span class="auto-label">{(autoResearchRate() * state.autoResearchThrottle / 100).toFixed(1)}/s</span>
                </div>
              </Show>
              <button 
                class={`action-btn tertiary ${!researchUnlocked() ? 'locked' : !canResearch() ? 'disabled' : ''}`}
                onClick={handleResearch}
              >
                <div class="btn-left">
                  <span class="btn-title">{t('researchAction')}</span>
                  <span class="btn-subtitle">
                    {!researchUnlocked() 
                      ? `${state.totalComputeSpent}/${BALANCE.RESEARCH_UNLOCK_THRESHOLD} compute`
                      : `${formatNumber(actions.getResearchComputeCost())} comp + ${formatNumber(actions.getResearchEnergyCost())} energy`
                    }
                  </span>
                </div>
                <span class="btn-cost">
                  {researchUnlocked() ? `+${researchEfficiency().toFixed(1)} RP` : ''}
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Middle Column: Hardware & Model Info */}
        <section class={`panel ${mobileTab() === 'hardware' ? 'mobile-visible' : 'mobile-hidden'}`} id="hardware-panel">
          <div class="panel-sticky-header">
            <h2>{t('hardware')}</h2>
            <div class="hardware-summary">
              <span class="hw-stat hw-stat-compute">💻 +{formatNumber(totalHwPower())}/s</span>
              <span class="hw-stat hw-stat-energy">⚡ -{formatNumber(totalHwEnergyUse())}/s</span>
            </div>
          </div>
          <div class="hardware-list">
            <For each={ownedHardwareList()}>
              {(hw) => {
                const canSell = createMemo(() => !state.paused && actions.canSellHardware(hw.id))
                const sellPrice = createMemo(() => actions.getHardwareSellPrice(hw.id))
                
                return (
                  <div class="hw-item">
                    <div class="hw-item-info">
                      <span class="hw-item-name">{hw.name}</span>
                      <span class="hw-item-stats">
                        <span class="hw-mini-stat compute">+{formatNumber(hw.computePerSec)}</span>
                        <span class="hw-mini-stat energy">-{formatNumber(hw.energyPerSec)}</span>
                      </span>
                    </div>
                    <Show when={canSell()}>
                      <button 
                        class="hw-sell-btn"
                        onClick={() => actions.sellHardware(hw.id)}
                        title={`${t('sellFor')} ${formatMoney(sellPrice())}`}
                      >
                        {t('sell')} {formatMoney(sellPrice())}
                      </button>
                    </Show>
                  </div>
                )
              }}
            </For>
          </div>
        </section>

        <section class={`panel ${mobileTab() === 'models' ? 'mobile-visible' : 'mobile-hidden'}`} id="model-panel">
          <h2>{t('training')}: {currentModel().name}</h2>
          <div id="model-info">
            <div class="model-name">{currentModel().name} <span class="model-params">({currentModel().params} {t('params')})</span></div>
            <div class="model-desc">{currentModel().description}</div>
            <div class="model-stats">
              <span>{t('maxUsers')}: {currentModel().maxUsers === Infinity ? '∞' : formatNumber(currentModel().maxUsers)}</span>
              <span>{t('trainCost2')}: {formatNumber(currentModel().trainComputeCost)} {t('compute').toLowerCase()}</span>
            </div>
          </div>

          <h2>{t('yourModels')}</h2>
          <div id="models-list">
            <For each={MODELS}>
              {(model, index) => {
                const unlocked = createMemo(() => state.unlockedModels.includes(model.id))
                const active = createMemo(() => state.currentModelIndex === index())
                const canUnlock = createMemo(() => !unlocked() && state.research >= model.researchCost)
                const previousUnlocked = createMemo(() => index() === 0 || state.unlockedModels.includes(MODELS[index() - 1].id))
                
                const modelState = createMemo(() => state.models[model.id])
                
                // Show model if: unlocked, or previous is unlocked (next model to unlock)
                const shouldShow = createMemo(() => {
                  if (unlocked()) return true
                  if (previousUnlocked()) return true
                  return false
                })

                // Progress towards unlocking (0-100%)
                const unlockProgress = createMemo(() => {
                  if (unlocked() || model.researchCost === 0) return 100
                  return Math.min(100, (state.research / model.researchCost) * 100)
                })

                const classes = createMemo(() => {
                  let c = 'model-item'
                  if (active()) c += ' active'
                  else if (unlocked()) c += ' unlocked'
                  else if (canUnlock()) c += ' unlockable'
                  else c += ' locked'
                  return c
                })

                const statusText = createMemo(() => {
                  if (!unlocked()) {
                    return `${formatNumber(state.research)}/${formatNumber(model.researchCost)} ${t('researchPoints')}`
                  }
                  return null // Handled by separate elements
                })

                const modelStats = createMemo(() => {
                  const ms = modelState()
                  if (!ms) return null
                  return {
                    quality: ms.quality.toFixed(0),
                    users: formatNumber(Math.floor(ms.users)),
                    training: active()
                  }
                })

                const canDiscontinue = createMemo(() => !state.paused && actions.canDiscontinueModel(model.id))

                return (
                  <Show when={shouldShow()}>
                    <div 
                      class={classes()}
                      onClick={() => {
                        if (!state.paused && (unlocked() || canUnlock())) {
                          actions.unlockModel(index())
                        }
                      }}
                    >
                      <div class="model-item-header">
                        <span class="model-item-name">{model.name}</span>
                        <Show when={statusText()}>
                          <span class="model-item-cost">{statusText()}</span>
                        </Show>
                        <Show when={modelStats()}>
                          <div class="model-item-stats">
                            <Show when={modelStats()?.training}>
                              <span class="model-stat training">{t('training').toUpperCase()}</span>
                            </Show>
                            <span class="model-stat quality">{modelStats()?.quality}%</span>
                            <span class="model-stat users">{modelStats()?.users} users</span>
                          </div>
                        </Show>
                        <Show when={canDiscontinue()}>
                          <button 
                            class="model-discontinue-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!state.paused && confirm(t('discontinueConfirm').replace('{model}', model.name))) {
                                actions.discontinueModel(model.id)
                              }
                            }}
                            title={t('discontinueConfirm').replace('{model}', model.name)}
                          >
                            ✕
                          </button>
                        </Show>
                      </div>
                      <Show when={!unlocked() && model.researchCost > 0}>
                        <div class="model-unlock-progress">
                          <div 
                            class={`model-unlock-fill ${canUnlock() ? 'ready' : ''}`}
                            style={{ width: `${unlockProgress()}%` }}
                          />
                        </div>
                      </Show>
                    </div>
                  </Show>
                )
              }}
            </For>
          </div>
        </section>

        {/* Right Column: Upgrades */}
        <section class={`panel ${mobileTab() === 'upgrades' ? 'mobile-visible' : 'mobile-hidden'}`} id="upgrades-panel">
          <div class="panel-sticky-header">
            <h2>{t('upgrades')}</h2>
            <div class="upgrade-tabs">
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'all' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('all')}
              >All</button>
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'energy' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('energy')}
              >⚡</button>
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'compute' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('compute')}
              >💻</button>
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'hw' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('hw')}
              >🖥️</button>
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'train' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('train')}
              >🎯</button>
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'research' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('research')}
              >🔬</button>
              <button 
                class={`upgrade-tab ${upgradeFilter() === 'auto' ? 'active' : ''}`}
                onClick={() => setUpgradeFilter('auto')}
              >🤖</button>
            </div>
          </div>
          <Show when={upgradesLocked()}>
            <div class="upgrades-locked-msg">
              {t('upgradesLockedMsg')}
            </div>
          </Show>
          <div id="upgrades-list">
            <For each={UPGRADE_TIERS}>
              {(tier) => {
                // Get upgrades for this tier
                const tierUpgrades = createMemo(() => 
                  UPGRADES.filter(u => getUpgradeTier(u) === tier.id)
                )
                
                // Check if tier is unlocked (has required model or no requirement)
                const tierUnlocked = createMemo(() => 
                  !tier.requiredModel || state.unlockedModels.includes(tier.requiredModel)
                )
                
                // Get current player tier (highest unlocked)
                const currentTierIndex = createMemo(() => {
                  let maxIndex = 0
                  for (let i = UPGRADE_TIERS.length - 1; i >= 0; i--) {
                    const t = UPGRADE_TIERS[i]
                    if (!t.requiredModel || state.unlockedModels.includes(t.requiredModel)) {
                      maxIndex = i
                      break
                    }
                  }
                  return maxIndex
                })
                
                const tierIndex = UPGRADE_TIERS.findIndex(t => t.id === tier.id)
                
                // Tier is inaccessible if 2+ levels below (cannot be expanded)
                const isInaccessible = createMemo(() => tierIndex < currentTierIndex() - 1)
                // Track if user has manually toggled the tier (null = not toggled)
                const [manualState, setManualState] = createSignal<boolean | null>(null)
                // Tier is collapsed if: inaccessible, OR user manually collapsed, OR (previous tier and user hasn't expanded)
                const isCollapsed = createMemo(() => {
                  if (isInaccessible()) return true
                  if (manualState() !== null) return manualState()
                  // Auto-collapse tier that is 1 level below current
                  return tierIndex === currentTierIndex() - 1
                })
                const toggleCollapsed = () => {
                  if (!isInaccessible()) {
                    setManualState(manualState() === null ? !isCollapsed() : !manualState())
                  }
                }
                
                // Count upgrades in this tier that match filter and are visible
                const visibleUpgradesInTier = createMemo(() => {
                  return tierUpgrades().filter(upgrade => {
                    const owned = actions.getUpgradeCount(upgrade.id)
                    const cost = actions.getUpgradeCost(upgrade)
                    const available = actions.isUpgradeAvailable(upgrade)
                    const maxed = upgrade.maxOwned !== -1 && owned >= upgrade.maxOwned
                    const isWithinReach = cost <= state.money * 2 || owned > 0
                    
                    // Match filter
                    const filter = upgradeFilter()
                    let matchesFilter = true
                    if (filter !== 'all') {
                      const type = upgrade.effect.type
                      switch (filter) {
                        case 'energy': matchesFilter = type === 'max_energy' || type === 'energy_regen'; break
                        case 'compute': matchesFilter = type === 'max_compute'; break
                        case 'hw': matchesFilter = type === 'hw_upgrade' || type === 'hw_boost'; break
                        case 'train': matchesFilter = type === 'train_efficiency'; break
                        case 'research': matchesFilter = type === 'research_efficiency'; break
                        case 'auto': matchesFilter = type === 'auto_inference'; break
                      }
                    }
                    if (!matchesFilter) return false
                    
                    // Visibility logic
                    if (owned > 0) return true
                    if (!available) {
                      if (upgrade.requires?.model && !state.unlockedModels.includes(upgrade.requires.model)) return false
                      if (upgrade.requires?.money && state.money < upgrade.requires.money * 0.8) return false
                    }
                    if (!isWithinReach && !maxed) return false
                    return true
                  }).length
                })
                
                // Count affordable upgrades in tier (for badge)
                const affordableInTier = createMemo(() => {
                  return tierUpgrades().filter(upgrade => {
                    const owned = actions.getUpgradeCount(upgrade.id)
                    const maxed = upgrade.maxOwned !== -1 && owned >= upgrade.maxOwned
                    if (maxed) return false
                    return !state.paused && actions.isUpgradeAvailable(upgrade) && actions.canAffordUpgrade(upgrade)
                  }).length
                })
                
                // Don't show tier if it's not unlocked or has no visible upgrades
                const shouldShowTier = createMemo(() => tierUnlocked() && visibleUpgradesInTier() > 0)
                
                return (
                  <Show when={shouldShowTier()}>
                    <div class="upgrade-tier">
                      <div 
                        class={`upgrade-tier-header ${isCollapsed() ? 'collapsed' : ''} ${isInaccessible() ? 'inaccessible' : ''}`}
                        onClick={toggleCollapsed}
                      >
                        <Show when={!isInaccessible()}>
                          <span class="tier-collapse-icon">{isCollapsed() ? '▶' : '▼'}</span>
                        </Show>
                        <span class="tier-name">{tier.name}</span>
                        <span class="tier-desc">{tier.description}</span>
                        <Show when={isInaccessible()}>
                          <span class="tier-obsolete">OBSOLETE</span>
                        </Show>
                        <Show when={affordableInTier() > 0 && isCollapsed() && !isInaccessible()}>
                          <span class="tier-badge">{affordableInTier()}</span>
                        </Show>
                      </div>
                      <Show when={!isCollapsed()}>
                        <div class="upgrade-tier-content">
                          <For each={tierUpgrades()}>
                            {(upgrade) => {
                              const owned = createMemo(() => actions.getUpgradeCount(upgrade.id))
                              const cost = createMemo(() => actions.getUpgradeCost(upgrade))
                              const available = createMemo(() => actions.isUpgradeAvailable(upgrade))
                              const affordable = createMemo(() => actions.canAffordUpgrade(upgrade))
                              const maxed = createMemo(() => upgrade.maxOwned !== -1 && owned() >= upgrade.maxOwned)

                              const isWithinReach = createMemo(() => cost() <= state.money * 2 || owned() > 0)

                              const matchesFilter = createMemo(() => {
                                const filter = upgradeFilter()
                                if (filter === 'all') return true
                                const type = upgrade.effect.type
                                switch (filter) {
                                  case 'energy': return type === 'max_energy' || type === 'energy_regen'
                                  case 'compute': return type === 'max_compute'
                                  case 'hw': return type === 'hw_upgrade' || type === 'hw_boost'
                                  case 'train': return type === 'train_efficiency'
                                  case 'research': return type === 'research_efficiency'
                                  case 'auto': return type === 'auto_inference'
                                  default: return true
                                }
                              })

                              const shouldShow = createMemo(() => {
                                if (!matchesFilter()) return false
                                if (owned() > 0) return true
                                if (!available()) {
                                  if (upgrade.requires?.model && !state.unlockedModels.includes(upgrade.requires.model)) {
                                    return false
                                  }
                                  if (upgrade.requires?.money && state.money < upgrade.requires.money * 0.8) {
                                    return false
                                  }
                                }
                                if (!isWithinReach()) return false
                                return true
                              })

                              const classes = createMemo(() => {
                                let c = 'upgrade-item'
                                if (maxed()) c += ' maxed'
                                else if (!available()) c += ' locked'
                                else if (!affordable()) c += ' cant-afford'
                                else c += ' affordable'
                                return c
                              })

                              const tag = createMemo(() => getTag(upgrade.effect.type))

                              const costText = createMemo(() => formatMoney(cost()))

                              const ownedText = createMemo(() => {
                                if (upgrade.maxOwned === 1) return owned() ? '✓' : ''
                                return owned() > 0 ? `${owned()}x` : ''
                              })

                              return (
                                <Show when={shouldShow()}>
                                  <div 
                                    class={classes()}
                                    onClick={() => !state.paused && actions.buyUpgrade(upgrade)}
                                  >
                                    <div class="upgrade-info">
                                      <div class="upgrade-name">
                                        <span class={`upgrade-tag ${tag().class}`}>{tag().text}</span>
                                        {upgrade.name}
                                      </div>
                                      <div class="upgrade-desc">{upgrade.description}</div>
                                    </div>
                                    <div class="upgrade-right">
                                      <div class="upgrade-cost">{maxed() ? t('max') : costText()}</div>
                                      <div class="upgrade-owned">{ownedText()}</div>
                                    </div>
                                  </div>
                                </Show>
                              )
                            }}
                          </For>
                        </div>
                      </Show>
                    </div>
                  </Show>
                )
              }}
            </For>
          </div>
        </section>
      </main>

      {/* Event Log */}
      <section class={`panel ${consoleCollapsed() ? 'collapsed' : ''}`} id="log-panel">
        <div class="log-header" onClick={() => setConsoleCollapsed(!consoleCollapsed())}>
          <h2>{t('eventLog')}</h2>
          <span class="collapse-icon">{consoleCollapsed() ? '▲' : '▼'}</span>
        </div>
        <Show when={!consoleCollapsed()}>
          <div id="event-log">
            <For each={eventLog.entries}>
              {(entry) => (
                <div class={`log-entry log-${entry.type}`}>
                  <span class="log-time">[{formatTime(entry.time)}]</span>
                  {entry.message}
                </div>
              )}
            </For>
            <Show when={eventLog.entries.length === 0}>
              <div class="log-entry log-info">{t('welcome')}</div>
            </Show>
          </div>
        </Show>
      </section>

      {/* Share Modal */}
      <Show when={showShareModal()}>
        <div class="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div class="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('shareTitle')}</h2>
            <Show when={shareImageUrl()}>
              <div class="share-image-container">
                <img src={shareImageUrl()!} alt="Share stats" class="share-image" />
              </div>
            </Show>
            <div class="share-buttons">
              <button class="share-btn-twitter" onClick={shareToTwitter}>
                𝕏 Twitter
              </button>
              <button class="share-btn-whatsapp" onClick={shareToWhatsApp}>
                WhatsApp
              </button>
              <button class="share-btn-copy" onClick={copyShareImage}>
                📋 Copy Image
              </button>
              <button class="share-btn-copy" onClick={copyShareText}>
                📝 Copy Text
              </button>
              <button class="share-btn-download" onClick={downloadShareImage}>
                💾 {t('shareDownload')}
              </button>
            </div>
            <button class="share-close-btn" onClick={() => setShowShareModal(false)}>
              ✕
            </button>
          </div>
        </div>
      </Show>

      {/* Feedback Modal */}
      <Show when={showFeedback()}>
        <div class="modal-overlay" onClick={() => { setShowFeedback(false); setFeedbackStatus('idle') }}>
          <div class="modal-content feedback-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('feedbackTitle')}</h2>
            <Show when={feedbackStatus() === 'success'}>
              <div class="feedback-success">{t('feedbackSuccess')}</div>
            </Show>
            <Show when={feedbackStatus() === 'error'}>
              <div class="feedback-error">{t('feedbackError')}</div>
            </Show>
            <Show when={feedbackStatus() === 'rateLimit'}>
              <div class="feedback-error">{t('feedbackRateLimit')}</div>
            </Show>
            <Show when={feedbackStatus() !== 'success'}>
              <textarea
                class="feedback-textarea"
                placeholder={t('feedbackPlaceholder')}
                value={feedbackMessage()}
                onInput={(e) => setFeedbackMessage(e.currentTarget.value)}
                rows={4}
              />
              <input
                type="email"
                class="feedback-email"
                placeholder={t('feedbackEmailPlaceholder')}
                value={feedbackEmail()}
                onInput={(e) => setFeedbackEmail(e.currentTarget.value)}
              />
              <div class="feedback-actions">
                <button 
                  class="feedback-cancel-btn" 
                  onClick={() => { setShowFeedback(false); setFeedbackStatus('idle') }}
                >
                  {t('feedbackClose')}
                </button>
                <button 
                  class={`feedback-send-btn ${feedbackStatus() === 'sending' ? 'sending' : ''}`}
                  onClick={handleSendFeedback}
                  disabled={feedbackMessage().trim().length < 3 || feedbackStatus() === 'sending'}
                >
                  {feedbackStatus() === 'sending' ? t('feedbackSending') : t('feedbackSend')}
                </button>
              </div>
            </Show>
          </div>
        </div>
      </Show>

      {/* Victory Modal */}
      <Show when={showVictoryModal()}>
        <div class="modal-overlay victory-overlay">
          <div class="modal-content victory-modal" onClick={(e) => e.stopPropagation()}>
            <div class="victory-icon">🎉</div>
            <h2 class="victory-title">{t('victoryTitle')}</h2>
            <p class="victory-subtitle">{t('victorySubtitle')}</p>
            <div class="victory-time">
              <span class="victory-time-label">{t('victoryTime')}:</span>
              <span class="victory-time-value">{formatTime(state.victoryTime || state.playTime)}</span>
            </div>
            <p class="victory-cta">{t('victoryCta')}</p>
            <div class="victory-social">
              <button class="share-btn-twitter" onClick={shareToTwitter}>
                𝕏 Twitter
              </button>
              <button class="share-btn-whatsapp" onClick={shareToWhatsApp}>
                WhatsApp
              </button>
            </div>
            <button class="victory-close-btn" onClick={() => setVictoryDismissed(true)}>
              {t('victoryClose')}
            </button>
          </div>
        </div>
      </Show>

      {/* Toast Container */}
      <div class="toast-container">
        <For each={toastStore.toasts}>
          {(toast) => (
            <div class={`toast toast-${toast.type}`}>
              <span class="toast-message">{toast.message}</span>
              <div class="toast-actions">
                <Show when={toast.shareText}>
                  <button 
                    class="toast-share-btn"
                    onClick={() => handleToastShare()}
                  >
                    📤
                  </button>
                </Show>
                <button 
                  class="toast-close-btn"
                  onClick={() => toastStore.remove(toast.id)}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </For>
      </div>

      {/* Tap Particles */}
      <For each={particles()}>
        {(particle) => (
          <div 
            class="tap-particle"
            style={{
              left: `${particle.x}px`,
              top: `${particle.y}px`,
              color: particle.color
            }}
          >
            {particle.emoji}
          </div>
        )}
      </For>
    </>
  )
}

export default App
